/**
 * src/pages/HomePage.js
 * Section order:
 *   1. Announcement Bar
 *   2. Hero Banner — 3-banner auto-sliding carousel
 *   3. Combo Packs (auto-scroll)
 *   4. Category Grid
 *   5. Featured Products
 *   6. Trust Section
 *   7. Urgency / Sale Banner
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { productsApi, unwrap } from '../services/api';
import ProductCard             from '../components/common/ProductCard';
import { ProductCardSkeleton } from '../components/common/ErrorBoundary';

/* ─── Scroll reveal ──────────────────────────────────────────────────────────── */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal, .reveal-scale');
    if (!els.length) return;
    const io = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/* ─── Announcement bar ───────────────────────────────────────────────────────── */
const TICKERS = [
  '🎆 Happy Diwali 2026 from Lakshmi Crackers!',
  '🔥 Mega Sale — Flat 80% OFF on All Products!',
  '⚡ Limited Stock — Order Before It Sells Out!',
  '✅ Quality Assured Crackers — Safe Packaging',
  '📞 Our team responds within 24 hours — Call Now!',
];
function AnnouncementBar() {
  const items = [...TICKERS, ...TICKERS];
  return (
    <div className="bg-gradient-to-r from-yellow-500 to-amber-400 py-2 overflow-hidden">
      <div className="flex animate-ticker whitespace-nowrap" style={{ width:'200%' }}>
        {items.map((t, i) => (
          <span key={i} className="inline-flex items-center gap-6 text-red-900 text-xs font-bold px-8">
            {t}<span className="w-1 h-1 rounded-full bg-red-900/30 inline-block"/>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─── SECTION 1: HERO BANNER ─────────────────────────────────────────────────── */
/* ─── SECTION 1: 3-BANNER AUTO-SLIDING HERO ─────────────────────────────────── */
// ── Date logic for sale phase ─────────────────────────────────────────────────
const SALE_START = new Date('2026-08-03T00:00:00+05:30');
const SALE_END   = new Date('2026-08-17T23:59:59+05:30');

function getSalePhase() {
  const now = new Date();
  if (now < SALE_START) return 'coming_soon';
  if (now >= SALE_START && now <= SALE_END) return 'live';
  return 'normal';
}

// ── Coming Soon Banners (before Aug 3rd) ──────────────────────────────────────
const BANNERS_COMING_SOON = [
  {
    id: 1,
    badge:        'Diwali 2026 — Lakshmi Crackers',
    title:        "Sivakasi's Finest",
    titleGold:    'Crackers — Now Online',
    saleLine:     'Flat 80% Off on All Sivakasi Crackers',
    urgency:      'Sale Starts Aug 3rd · 10 AM · Pan-India Delivery Available',
    highlights:   ['Flat 80% Off', 'Quality Certified', 'Pan-India Delivery'],
    cta:          { label: 'Shop Now',          to: '/products' },
    ctaSecondary: { label: 'View Gift Boxes',   to: '/products?category=gift-boxes' },
    bg:           'from-red-950 via-red-900 to-rose-900',
    accent:       '#f5c842',
  },
  {
    id: 2,
    badge:        'Sky Shots · Rockets · Sparklers · Combo Packs',
    title:        'Premium Sivakasi',
    titleGold:    'Crackers Online',
    saleLine:     'Direct from Sivakasi — Flat 80% Off on Everything',
    urgency:      'Sky Shots · Rockets · Gift Boxes · Combo Packs — All at 80% Off',
    highlights:   ['Direct from Sivakasi', 'Quality Certified', 'Safe Packaging'],
    cta:          { label: 'See All Products',   to: '/products' },
    ctaSecondary: { label: 'Combo Packs',        to: '/products?category=combo-packs' },
    bg:           'from-indigo-950 via-slate-900 to-red-950',
    accent:       '#fde68a',
  },
  {
    id: 3,
    badge:        'Corporate Diwali Gifting — Lakshmi Crackers',
    title:        'Gift Your Team',
    titleGold:    'This Diwali',
    saleLine:     'Premium Branded Gift Boxes — Delivered Across India',
    urgency:      'Pan-India Delivery · Minimum 10 Boxes · Quick Dispatch',
    highlights:   ['Custom Branding', 'Pan-India Delivery', 'Min 10 Boxes'],
    cta:          { label: 'Send Enquiry',       to: '/contact' },
    ctaSecondary: { label: 'View Gift Boxes',    to: '/products?category=gift-boxes' },
    bg:           'from-slate-950 via-zinc-900 to-slate-900',
    accent:       '#fde68a',
  },
];

const BANNERS_LIVE = [
  {
    id: 1,
    badge:        'Sale Live — August 3rd to August 17th Only',
    title:        'The Big Diwali',
    titleGold:    'Sale is Live',
    saleLine:     'Flat 80% Off on All Sivakasi Crackers',
    urgency:      'Pan-India Delivery Available · Offer Ends August 17th',
    highlights:   ['80% + Extra 10% Off', 'Pan-India Delivery', 'Ends Aug 17'],
    cta:          { label: 'Shop Now',           to: '/products' },
    ctaSecondary: { label: 'View Gift Boxes',    to: '/products?category=gift-boxes' },
    bg:           'from-red-950 via-red-900 to-rose-900',
    accent:       '#f5c842',
  },
  {
    id: 2,
    badge:        'Sky Shots · Rockets · Sparklers · Combo Packs',
    title:        'Biggest Diwali',
    titleGold:    'Sale of the Year',
    saleLine:     'Flat 80% Off on All Sivakasi Crackers',
    urgency:      'Limited stock · Selling fast · Order before it runs out',
    highlights:   ['Direct from Sivakasi', 'Safe Packaging', 'Ends Aug 17'],
    cta:          { label: 'Shop Products',      to: '/products' },
    ctaSecondary: { label: 'Combo Packs',        to: '/products?category=combo-packs' },
    bg:           'from-amber-950 via-orange-950 to-red-950',
    accent:       '#fde68a',
  },
  {
    id: 3,
    badge:        'Corporate Gifting — Limited Period Offer',
    title:        'Diwali Gifts',
    titleGold:    'for Your Team',
    saleLine:     'Premium Gift Boxes · Extra 10% Off till Aug 17',
    urgency:      'Pan-India Delivery · Minimum 10 Boxes · Quick Dispatch',
    highlights:   ['Custom Branding', 'Pan-India Delivery', 'Ends Aug 17'],
    cta:          { label: 'Send Enquiry',       to: '/contact' },
    ctaSecondary: { label: 'View Gift Boxes',    to: '/products?category=gift-boxes' },
    bg:           'from-slate-950 via-zinc-900 to-slate-900',
    accent:       '#fde68a',
  },
];

const BANNERS_NORMAL = [
  {
    id: 1,
    badge:        'Diwali 2026 — Lakshmi Crackers',
    title:        'Celebrate Diwali',
    titleGold:    'the Sivakasi Way',
    saleLine:     'Premium Sivakasi Crackers — Flat 80% Off on All Products',
    urgency:      'Limited stock · Order now for guaranteed Diwali delivery',
    highlights:   ['Flat 80% Off', 'Combo Packs from ₹2,999', 'Pan-India Delivery'],
    cta:          { label: 'Shop Now',           to: '/products' },
    ctaSecondary: { label: 'View Gift Boxes',    to: '/products?category=gift-boxes' },
    bg:           'from-red-950 via-red-900 to-rose-900',
    accent:       '#f5c842',
  },
  {
    id: 2,
    badge:        'Sky Shots · Rockets · Sparklers · Combo Packs',
    title:        'Light Up Your',
    titleGold:    'Diwali Night',
    saleLine:     'Flat 80% Off on Premium Sky Shots and Combo Packs',
    urgency:      'Direct from Sivakasi · Safe packaging · Fast delivery',
    highlights:   ['Premium Sky Shots', 'Combo from 2,999', 'Safe Packaging'],
    cta:          { label: 'Shop Sky Shots',     to: '/products?category=sky-shots' },
    ctaSecondary: { label: 'View Combo Packs',   to: '/products?category=combo-packs' },
    bg:           'from-indigo-950 via-purple-950 to-red-950',
    accent:       '#fde68a',
  },
  {
    id: 3,
    badge:        'Corporate Diwali Gifting — Lakshmi Crackers',
    title:        'Make Diwali',
    titleGold:    'Memorable',
    saleLine:     'Premium Gift Boxes for Your Team and Clients',
    urgency:      'Pan-India Delivery · Minimum 10 Boxes · Quick Dispatch',
    highlights:   ['Custom Branding', 'Pan-India Delivery', 'Min 10 Boxes'],
    cta:          { label: 'Send Enquiry',       to: '/contact' },
    ctaSecondary: { label: 'View Gift Boxes',    to: '/products?category=gift-boxes' },
    bg:           'from-slate-950 via-zinc-900 to-slate-900',
    accent:       '#fde68a',
  },
];

// ── Pick correct banner set based on current date ─────────────────────────────
function getBanners() {
  const phase = getSalePhase();
  if (phase === 'coming_soon') return BANNERS_COMING_SOON;
  if (phase === 'live')        return BANNERS_LIVE;
  return BANNERS_NORMAL;
}

const BANNERS = getBanners();

/* ── Skyshot burst keyframes injected via style tag in component ── */
function BannerSlider() {
  const navigate = useNavigate();
  const [current,   setCurrent]   = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState('left');
  const timerRef = useRef(null);

  const goTo = useCallback((idx, dir) => {
    if (animating) return;
    setDirection(dir || 'left');
    setAnimating(true);
    setTimeout(() => { setCurrent(idx); setAnimating(false); }, 450);
  }, [animating]);

  const next = useCallback(() => goTo((current + 1) % BANNERS.length, 'left'),  [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + BANNERS.length) % BANNERS.length, 'right'), [current, goTo]);

  useEffect(() => {
    timerRef.current = setInterval(next, 5000);
    return () => clearInterval(timerRef.current);
  }, [next]);

  const [timeLeft, setTimeLeft] = useState({ days:0, hours:0, mins:0, secs:0 });
  const [bookingTimeLeft, setBookingTimeLeft] = useState({ days:0, hours:0, mins:0, secs:0 });
  const phase = getSalePhase();

  useEffect(() => {
    if (phase !== 'coming_soon') return;
    const t0 = new Date('2026-08-03T10:00:00+05:30');
    const tick = () => {
      const d = t0 - new Date();
      if (d <= 0) return;
      setTimeLeft({ days:Math.floor(d/86400000), hours:Math.floor((d%86400000)/3600000), mins:Math.floor((d%3600000)/60000), secs:Math.floor((d%60000)/1000) });
    };
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, [phase]); // eslint-disable-line

  // Booking closes Oct 31 countdown — always running
  useEffect(() => {
    const deadline = new Date('2026-10-31T23:59:59+05:30');
    const tick = () => {
      const d = deadline - new Date();
      if (d <= 0) { setBookingTimeLeft({ days:0, hours:0, mins:0, secs:0 }); return; }
      setBookingTimeLeft({ days:Math.floor(d/86400000), hours:Math.floor((d%86400000)/3600000), mins:Math.floor((d%3600000)/60000), secs:Math.floor((d%60000)/1000) });
    };
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, []);

  const b    = BANNERS[current];
  const slip = animating ? (direction==='left' ? 'opacity-0 translate-x-4' : 'opacity-0 -translate-x-4') : 'opacity-100 translate-x-0';

  return (
    <section className="relative w-full overflow-hidden select-none"
      style={{ minHeight:'38vh', background:'#0c0005' }}>

      <style>{`
        @keyframes cd-pulse { 0%,100%{opacity:.85} 50%{opacity:1} }
        @keyframes gold-shimmer { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
        @keyframes fade-up { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spark-float {
          0%   { transform:translate(0,0) scale(1); opacity:.8; }
          100% { transform:translate(var(--tx),var(--ty)) scale(0); opacity:0; }
        }
        @keyframes twinkle { 0%,100%{opacity:0;transform:scale(.3)} 50%{opacity:.7;transform:scale(1)} }
      `}</style>

      {/* ── Gradient background ─────────────────────────────────────────── */}
      <div className={`absolute inset-0 transition-all duration-700 bg-gradient-to-br ${b.bg}`}/>

      {/* ── Subtle vignette edges ──────────────────────────────────────── */}
      <div className="absolute inset-0" style={{background:'radial-gradient(ellipse at center,transparent 60%,rgba(0,0,0,.45) 100%)'}}/>

      {/* ── Gold top line ──────────────────────────────────────────────── */}
      <div className="absolute top-0 left-0 right-0" style={{height:'3px',background:'linear-gradient(90deg,transparent 0%,#d97706 20%,#f5c842 50%,#d97706 80%,transparent 100%)'}}/>

      {/* ── Floating sparkles ─────────────────────────────────────────── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[
          {x:'8%',  y:'20%', c:'#f5c842', s:'5px', d:'0s',   dur:'3.2s', tx:'-20px', ty:'-70px'},
          {x:'88%', y:'18%', c:'#ef4444', s:'4px', d:'0.7s', dur:'3.5s', tx:'18px',  ty:'-80px'},
          {x:'5%',  y:'55%', c:'#a855f7', s:'4px', d:'1.4s', dur:'3.8s', tx:'-12px', ty:'-65px'},
          {x:'92%', y:'48%', c:'#34d399', s:'4px', d:'0.4s', dur:'3.3s', tx:'15px',  ty:'-72px'},
          {x:'15%', y:'75%', c:'#fde68a', s:'3px', d:'1.8s', dur:'3.6s', tx:'-18px', ty:'-60px'},
          {x:'82%', y:'70%', c:'#fb923c', s:'3px', d:'1.1s', dur:'3.4s', tx:'20px',  ty:'-68px'},
          {x:'48%', y:'10%', c:'#f5c842', s:'5px', d:'2.2s', dur:'3.7s', tx:'-8px',  ty:'-75px'},
          {x:'25%', y:'30%', c:'#60a5fa', s:'3px', d:'0.9s', dur:'3.1s', tx:'-15px', ty:'-58px'},
          {x:'72%', y:'25%', c:'#f5c842', s:'3px', d:'2.5s', dur:'3.9s', tx:'12px',  ty:'-62px'},
          {x:'35%', y:'80%', c:'#fde68a', s:'3px', d:'1.6s', dur:'3.2s', tx:'-10px', ty:'-55px'},
          {x:'65%', y:'82%', c:'#ef4444', s:'4px', d:'0.3s', dur:'3.5s', tx:'16px',  ty:'-70px'},
          {x:'55%', y:'60%', c:'#a855f7', s:'3px', d:'2.0s', dur:'3.3s', tx:'-14px', ty:'-60px'},
        ].map((p,i)=>(
          <div key={i} style={{
            position:'absolute', left:p.x, top:p.y,
            width:p.s, height:p.s, borderRadius:'50%',
            background:p.c, boxShadow:`0 0 ${parseInt(p.s)*3}px ${p.c}`,
            '--tx':p.tx, '--ty':p.ty,
            animation:`spark-float ${p.dur} ease-out infinite`,
            animationDelay:p.d,
          }}/>
        ))}
        {/* Twinkling stars */}
        {[
          {x:'20%',y:'12%',s:'6px',c:'#fde68a',d:'0s',dur:'2.1s'},
          {x:'78%',y:'8%', s:'5px',c:'#f5c842',d:'0.8s',dur:'1.8s'},
          {x:'12%',y:'40%',s:'4px',c:'#fff',   d:'1.5s',dur:'2.4s'},
          {x:'88%',y:'35%',s:'4px',c:'#fff',   d:'0.5s',dur:'1.9s'},
          {x:'50%',y:'5%', s:'6px',c:'#f5c842',d:'1.2s',dur:'2.2s'},
          {x:'32%',y:'90%',s:'4px',c:'#fde68a',d:'2.0s',dur:'1.7s'},
          {x:'68%',y:'88%',s:'4px',c:'#fde68a',d:'0.3s',dur:'2.3s'},
        ].map((s,i)=>(
          <div key={'t'+i} style={{
            position:'absolute', left:s.x, top:s.y,
            width:s.s, height:s.s, borderRadius:'50%',
            background:s.c, boxShadow:`0 0 ${parseInt(s.s)*2}px ${s.c}`,
            animation:`twinkle ${s.dur} ease-in-out infinite`,
            animationDelay:s.d,
          }}/>
        ))}
      </div>

      {/* ── Main content ───────────────────────────────────────────────── */}
      <div className={`relative flex flex-col items-center justify-center text-center
        px-5 sm:px-10 lg:px-20 py-5 sm:py-7 lg:py-8
        transition-all duration-450 ease-out ${slip}`}
        style={{ minHeight:'38vh', zIndex:10 }}>

        {/* Badge */}
        <p className="mb-3 text-xs sm:text-sm font-semibold tracking-wide uppercase"
          style={{ color:'rgba(253,230,138,0.75)', letterSpacing:'0.12em',
            fontFamily:'"Segoe UI","Helvetica Neue",Arial,sans-serif',
            animation:'fade-up .5s ease both', animationDelay:'.05s' }}>
          {b.badge}
        </p>

        {/* Main title */}
        <h1 className="mb-3" style={{ animation:'fade-up .5s ease both', animationDelay:'.1s' }}>
          <span className="block font-bold text-white leading-tight"
            style={{ fontSize:'clamp(1.6rem,4.5vw,3rem)',
              fontFamily:'"Playfair Display","Georgia",serif',
              textShadow:'0 2px 16px rgba(0,0,0,.4)' }}>
            {b.title}
          </span>
          <span className="block font-bold leading-tight"
            style={{ fontSize:'clamp(1.7rem,5vw,3.2rem)',
              fontFamily:'"Playfair Display","Georgia",serif',
              background:'linear-gradient(135deg,#fde68a 0%,#f5c842 40%,#d97706 70%,#f5c842 100%)',
              backgroundSize:'200% 200%',
              WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text',
              animation:'gold-shimmer 4s ease infinite',
              filter:'drop-shadow(0 0 12px rgba(245,200,66,.35))' }}>
            {b.titleGold}
          </span>
        </h1>

        {/* Sale line */}
        <p className="mb-2 font-semibold text-white text-sm sm:text-base"
          style={{ fontFamily:'"Segoe UI","Helvetica Neue",Arial,sans-serif',
            textShadow:'0 1px 8px rgba(0,0,0,.4)',
            animation:'fade-up .5s ease both', animationDelay:'.15s' }}>
          {b.saleLine}
        </p>

        {/* Urgency */}
        <p className="mb-3 text-xs sm:text-sm font-medium"
          style={{ color:b.accent, opacity:.85,
            fontFamily:'"Segoe UI","Helvetica Neue",Arial,sans-serif',
            animation:'fade-up .5s ease both', animationDelay:'.18s' }}>
          {b.urgency}
        </p>

        {/* ── Booking Closes Banner + Countdown ── */}
        <div className="mb-4 w-full max-w-sm mx-auto"
          style={{ animation:'fade-up .5s ease both', animationDelay:'.2s' }}>
          {/* Message pill */}
          <div className="flex items-center justify-center gap-2 mb-3 px-4 py-2 rounded-full mx-auto w-fit"
            style={{
              background:'linear-gradient(135deg,rgba(185,28,28,.85),rgba(220,38,38,.75))',
              border:'1.5px solid rgba(245,200,66,.5)',
              boxShadow:'0 0 20px rgba(220,38,38,.4)',
            }}>
            <span style={{fontSize:'1rem'}}>🚨</span>
            <p style={{
              fontFamily:'"Segoe UI","Helvetica Neue",Arial,sans-serif',
              fontWeight:700, fontSize:'clamp(.65rem,1.8vw,.8rem)',
              color:'#fff', letterSpacing:'.02em', textAlign:'center',
            }}>
              Hurry Up! Grab your favourite crackers — Bookings close <span style={{color:'#fde68a'}}>October 31st</span>
            </p>
          </div>

          {/* Countdown tiles */}
          <div className="flex items-end justify-center gap-2">
            {[
              {v:bookingTimeLeft.days,  l:'Days'},
              {v:bookingTimeLeft.hours, l:'Hrs'},
              {v:bookingTimeLeft.mins,  l:'Min'},
              {v:bookingTimeLeft.secs,  l:'Sec'},
            ].map(({v,l},i)=>(
              <div key={l} className="flex items-end gap-2">
                <div className="flex flex-col items-center">
                  <div className="flex items-center justify-center rounded-lg relative overflow-hidden"
                    style={{
                      width:'clamp(42px,7vw,56px)', height:'clamp(46px,8vw,60px)',
                      background:'linear-gradient(160deg,rgba(20,5,0,.95),rgba(45,10,5,.9))',
                      boxShadow:'0 0 0 1px rgba(245,200,66,.5),0 4px 16px rgba(0,0,0,.6)',
                      animation:'cd-pulse 2s ease-in-out infinite',
                      animationDelay:`${i*.25}s`,
                    }}>
                    <div style={{position:'absolute',top:0,left:0,right:0,height:'45%',background:'linear-gradient(180deg,rgba(255,255,255,.05),transparent)'}}/>
                    <div style={{position:'absolute',top:'50%',left:'6px',right:'6px',height:'1px',background:'rgba(0,0,0,.55)'}}/>
                    <span style={{
                      fontFamily:'"Courier New","Lucida Console",monospace',
                      fontWeight:700,
                      fontSize:'clamp(1.1rem,2.8vw,1.6rem)',
                      background:'linear-gradient(180deg,#fff 0%,#fde68a 35%,#f5c842 60%,#d97706 100%)',
                      WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text',
                      filter:'drop-shadow(0 0 6px rgba(245,200,66,.6))',
                      fontVariantNumeric:'tabular-nums', zIndex:2, position:'relative',
                    }}>
                      {String(v).padStart(2,'0')}
                    </span>
                  </div>
                  <span style={{
                    marginTop:'4px', fontSize:'clamp(.4rem,.9vw,.55rem)',
                    fontFamily:'"Segoe UI","Helvetica Neue",Arial,sans-serif',
                    fontWeight:600, letterSpacing:'.12em',
                    color:'rgba(253,230,138,.6)', textTransform:'uppercase',
                  }}>
                    {l}
                  </span>
                </div>
                {i < 3 && (
                  <div className="flex flex-col gap-1 pb-6">
                    {[0,1].map(d=>(
                      <div key={d} style={{
                        width:'3px', height:'3px', borderRadius:'50%',
                        background:'#f5c842', boxShadow:'0 0 5px rgba(245,200,66,.8)',
                        animation:'cd-pulse 1.2s ease-in-out infinite',
                        animationDelay:`${d*.4+i*.15}s`,
                      }}/>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Grand Countdown ── */}
        {phase === 'coming_soon' && (
          <div className="mb-4 w-full max-w-xs mx-auto"
            style={{ animation:'fade-up .5s ease both', animationDelay:'.2s' }}>

            <p className="text-center mb-2 font-semibold text-xs tracking-widest uppercase"
              style={{ color:'rgba(253,230,138,.65)',
                fontFamily:'"Segoe UI","Helvetica Neue",Arial,sans-serif',
                letterSpacing:'0.2em' }}>
              Sale Starts In
            </p>

            <div className="flex items-end justify-center gap-2 sm:gap-3">
              {[{v:timeLeft.days,l:'Days'},{v:timeLeft.hours,l:'Hrs'},{v:timeLeft.mins,l:'Min'},{v:timeLeft.secs,l:'Sec'}].map(({v,l},i)=>(
                <div key={l} className="flex items-end gap-2 sm:gap-3">
                  <div className="flex flex-col items-center">
                    {/* Number tile */}
                    <div className="flex items-center justify-center rounded-xl"
                      style={{
                        width:'clamp(52px,9vw,68px)', height:'clamp(56px,10vw,72px)',
                        background:'linear-gradient(160deg,rgba(20,5,0,.95),rgba(45,10,5,.9))',
                        boxShadow:`0 0 0 1px rgba(245,200,66,.5), 0 4px 24px rgba(0,0,0,.6), 0 0 20px rgba(245,200,66,.12)`,
                        animation:'cd-pulse 2s ease-in-out infinite',
                        animationDelay:`${i*.25}s`,
                        position:'relative', overflow:'hidden',
                      }}>
                      {/* inner top shine */}
                      <div style={{position:'absolute',top:0,left:0,right:0,height:'45%',background:'linear-gradient(180deg,rgba(255,255,255,.04),transparent)',borderRadius:'12px 12px 0 0'}}/>
                      {/* center split */}
                      <div style={{position:'absolute',top:'50%',left:'8px',right:'8px',height:'1px',background:'rgba(0,0,0,.6)'}}/>
                      <span style={{
                        fontFamily:'"Courier New","Lucida Console",monospace',
                        fontWeight:700,
                        fontSize:'clamp(1.4rem,3.5vw,2rem)',
                        background:'linear-gradient(180deg,#fff 0%,#fde68a 35%,#f5c842 60%,#d97706 100%)',
                        WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text',
                        filter:'drop-shadow(0 0 8px rgba(245,200,66,.6))',
                        fontVariantNumeric:'tabular-nums', zIndex:2, position:'relative',
                      }}>
                        {String(v).padStart(2,'0')}
                      </span>
                    </div>
                    <span style={{
                      marginTop:'5px', fontSize:'clamp(.45rem,1vw,.6rem)',
                      fontFamily:'"Segoe UI","Helvetica Neue",Arial,sans-serif',
                      fontWeight:600, letterSpacing:'.14em',
                      color:'rgba(253,230,138,.6)', textTransform:'uppercase',
                    }}>
                      {l}
                    </span>
                  </div>
                  {i < 3 && (
                    <div className="flex flex-col gap-1.5 pb-7">
                      {[0,1].map(d=>(
                        <div key={d} style={{
                          width:'4px', height:'4px', borderRadius:'50%',
                          background:'#f5c842', boxShadow:'0 0 6px rgba(245,200,66,.8)',
                          animation:'cd-pulse 1.2s ease-in-out infinite',
                          animationDelay:`${d*.4+i*.15}s`,
                        }}/>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <p className="text-center mt-2 text-xs font-medium"
              style={{ color:'rgba(253,230,138,.55)',
                fontFamily:'"Segoe UI","Helvetica Neue",Arial,sans-serif',
                letterSpacing:'.06em' }}>
              Aug 3, 2026 · 10:00 AM IST
            </p>
          </div>
        )}

        {/* Highlights */}
        <div className="flex flex-wrap justify-center gap-2 mb-4"
          style={{ animation:'fade-up .5s ease both', animationDelay:'.22s' }}>
          {b.highlights.map((h,i)=>(
            <span key={i} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full"
              style={{
                fontFamily:'"Segoe UI","Helvetica Neue",Arial,sans-serif',
                background:'rgba(255,255,255,.07)',
                border:`1px solid ${b.accent}33`,
                color:b.accent, letterSpacing:'.04em',
              }}>
              <span style={{width:'5px',height:'5px',borderRadius:'50%',background:b.accent,display:'inline-block',flexShrink:0}}/>
              {h}
            </span>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center w-full max-w-xs sm:max-w-none px-4 sm:px-0"
          style={{ animation:'fade-up .5s ease both', animationDelay:'.26s' }}>
          <button onClick={()=>navigate(b.cta.to)}
            className="font-semibold py-3 px-8 sm:px-10 rounded-full text-sm text-white
              transition-all duration-300 hover:-translate-y-0.5 active:scale-95 w-full sm:w-auto"
            style={{
              fontFamily:'"Segoe UI","Helvetica Neue",Arial,sans-serif',
              letterSpacing:'.04em',
              background:'linear-gradient(135deg,#b91c1c,#dc2626)',
              boxShadow:'0 6px 20px rgba(185,28,28,.45),0 0 0 1px rgba(245,200,66,.2)',
            }}>
            {b.cta.label}
          </button>
          <button onClick={()=>navigate(b.ctaSecondary.to)}
            className="font-semibold py-3 px-8 sm:px-10 rounded-full text-sm
              transition-all duration-300 hover:-translate-y-0.5 active:scale-95
              backdrop-blur-sm w-full sm:w-auto"
            style={{
              fontFamily:'"Segoe UI","Helvetica Neue",Arial,sans-serif',
              letterSpacing:'.04em',
              background:'rgba(255,255,255,.06)',
              border:`1.5px solid ${b.accent}55`,
              color:b.accent,
            }}>
            {b.ctaSecondary.label}
          </button>
        </div>

        {/* Slide dots */}
        <div className="flex gap-2 mt-5 justify-center">
          {BANNERS.map((_,i)=>(
            <button key={i} onClick={()=>goTo(i)}
              style={{
                width:current===i?'24px':'6px', height:'6px',
                borderRadius:'3px',
                background:current===i?'#f5c842':'rgba(255,255,255,.25)',
                transition:'all .35s ease',
                boxShadow:current===i?'0 0 8px rgba(245,200,66,.5)':'none',
                border:'none', cursor:'pointer', padding:0,
              }}/>
          ))}
        </div>
      </div>

      {/* Prev / Next arrows */}
      {[{dir:'prev',icon:'M15 19l-7-7 7-7',pos:'left-3 lg:left-5'},{dir:'next',icon:'M9 5l7 7-7 7',pos:'right-3 lg:right-5'}].map(({dir,icon,pos})=>(
        <button key={dir} onClick={dir==='prev'?prev:next}
          className={`hidden sm:flex absolute ${pos} top-1/2 -translate-y-1/2 w-9 h-9
            items-center justify-center rounded-full z-20 backdrop-blur-sm
            transition-all duration-300 hover:scale-110 active:scale-95`}
          style={{background:'rgba(0,0,0,.3)',border:'1px solid rgba(245,200,66,.3)',color:'#f5c842'}}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon}/>
          </svg>
        </button>
      ))}
    </section>
  );
}



/* ─── SECTION 2: COMBO PACKS DATA ────────────────────────────────────────────── */
const COMBOS = [
  {
    id: 1,
    name:     "Lakshmi's Celebration Pack",
    tagline:  'Perfect starter pack for families',
    subtitle: 'Sound + Light + Full fun combo 💥',
    price:    '₹2,999',
    emoji:    '🎆',
    badge:    'POPULAR',
    badgeColor: 'bg-blue-500',
    gradient: 'from-blue-900 via-blue-800 to-indigo-900',
    glow:     'shadow-blue-900/50',
  },
  {
    id: 2,
    name:     "Lakshmi's Dhamaka Pack",
    tagline:  'Excitement and energy for everyone',
    subtitle: 'Full set for family + friends celebration 🎉',
    price:    '₹4,999',
    emoji:    '🎇',
    badge:    'BESTSELLER',
    badgeColor: 'bg-yellow-500',
    gradient: 'from-amber-800 via-orange-800 to-red-900',
    glow:     'shadow-orange-900/50',
  },
  {
    id: 3,
    name:     "Lakshmi's Premium Utsav",
    tagline:  'Prosperity and grandeur this Diwali',
    subtitle: 'Full skyshots + premium crackers set 🚀',
    price:    '₹6,999',
    emoji:    '🪔',
    badge:    'PREMIUM',
    badgeColor: 'bg-purple-600',
    gradient: 'from-purple-900 via-violet-800 to-indigo-900',
    glow:     'shadow-purple-900/50',
  },
  {
    id: 4,
    name:     "Lakshmi's Royal Dhamaka",
    tagline:  'Premium and powerful celebration',
    subtitle: 'Royal crackers + sky shots + grand display 👑',
    price:    '₹9,999',
    emoji:    '🎊',
    badge:    '🔥 HOT',
    badgeColor: 'bg-gradient-to-r from-orange-600 to-red-600',
    gradient: 'from-rose-900 via-red-800 to-orange-900',
    glow:     'shadow-rose-900/50',
  },
  {
    id: 5,
    name:     "Lakshmi's Grand Utsav",
    tagline:  'The ultimate Diwali experience',
    subtitle: 'Grand celebration. Maximum fireworks. Unlimited happiness 💥',
    price:    '₹14,999',
    emoji:    '👑',
    badge:    '✨ ULTIMATE',
    badgeColor: 'bg-gradient-to-r from-red-600 to-amber-500',
    gradient: 'from-red-900 via-rose-800 to-pink-900',
    glow:     'shadow-red-900/50',
  },
];
const COMBOS_LOOP = [...COMBOS, ...COMBOS, ...COMBOS];

function ComboPacks() {
  const navigate  = useNavigate();
  const trackRef  = useRef(null);
  const posRef    = useRef(0);
  const rafRef    = useRef(null);
  const pausedRef = useRef(false);
  const CARD_W = 340, SET_W = COMBOS.length * CARD_W;

  useEffect(() => {
    const animate = () => {
      if (!pausedRef.current) {
        posRef.current += 0.55;
        if (posRef.current >= SET_W) posRef.current = 0;
        if (trackRef.current) trackRef.current.style.transform = `translateX(-${posRef.current}px)`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [SET_W]);

  return (
    <section className="py-14 overflow-hidden"
      style={{background:'linear-gradient(135deg,#1a0505 0%,#2d0a0a 50%,#1a0505 100%)'}}>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <div className="text-center reveal">
          <p className="text-yellow-400/70 text-xs font-black uppercase tracking-widest mb-3">🎆 Expert Curated</p>
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-4"
            style={{fontFamily:'"Playfair Display",serif'}}>
            Our Combo Packs
          </h2>
          <div className="max-w-2xl mx-auto bg-yellow-400/10 border border-yellow-400/25 rounded-2xl px-6 py-4">
            <p className="text-yellow-200/90 text-sm sm:text-base leading-relaxed">
              🤔 <strong>Confused which products to pick?</strong> Don't overthink it!
              Our expert-curated combo packs are designed for every budget.
              {' '}<span className="text-yellow-300 font-bold">Our team can customise any combo</span>{' '}
              as per your expectations — just reach out! 🎆
            </p>
          </div>
        </div>
      </div>

      <div className="relative"
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; }}>
        <div className="absolute left-0 top-0 bottom-0 w-20 z-10 pointer-events-none"
          style={{background:'linear-gradient(to right, #1a0505, transparent)'}}/>
        <div className="absolute right-0 top-0 bottom-0 w-20 z-10 pointer-events-none"
          style={{background:'linear-gradient(to left, #1a0505, transparent)'}}/>
        <div className="overflow-hidden">
          <div ref={trackRef} className="flex gap-5 py-4" style={{width:'max-content', willChange:'transform'}}>
            {COMBOS_LOOP.map((combo, i) => {
              return (
                <div key={`${combo.id}-${i}`}
                  onClick={() => navigate('/products?category=combo-packs')}
                  className={`relative flex-shrink-0 w-[300px] sm:w-[320px] rounded-3xl overflow-hidden cursor-pointer
                    bg-gradient-to-br ${combo.gradient} shadow-2xl ${combo.glow}
                    hover:scale-[1.03] hover:-translate-y-1 transition-all duration-300 group`}>
                  <div className={`absolute top-4 right-4 z-10 ${combo.badgeColor} text-white text-[10px] font-black px-3 py-1 rounded-full shadow-lg`}>
                    {combo.badge}
                  </div>
                  <div className="p-6 pt-7">
                    <div className="text-5xl mb-3 group-hover:scale-110 transition-transform duration-300 inline-block"
                      style={{filter:'drop-shadow(0 4px 12px rgba(0,0,0,0.4))'}}>
                      {combo.emoji}
                    </div>
                    <h3 className="font-black text-white text-lg leading-tight mb-2" style={{fontFamily:'"Plus Jakarta Sans",sans-serif'}}>
                      {combo.name}
                    </h3>
                    <p className="text-white/80 text-xs leading-relaxed mb-1.5">"{combo.tagline}"</p>
                    <p className="text-white/70 text-xs mb-5">{combo.subtitle}</p>
                    <div className="bg-black/30 rounded-2xl px-4 py-4 mb-4 text-center">
                      <p className="text-yellow-300/70 text-[10px] font-black uppercase tracking-wider mb-1">Our Price</p>
                      <p className="text-yellow-300 font-black text-3xl leading-none" style={{fontFamily:'"Plus Jakarta Sans",sans-serif'}}>{combo.price}</p>
                    </div>
                    <button className="w-full bg-white/15 hover:bg-yellow-400/20 border border-white/25
                      hover:border-yellow-400/40 text-white hover:text-yellow-200 font-black py-2.5 rounded-xl text-sm
                      transition-all duration-200">
                      Enquire Now →
                    </button>
                  </div>
                  <div className="absolute -bottom-8 -right-8 w-32 h-32 rounded-full bg-white/5 pointer-events-none"/>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="text-center mt-10">
        <button onClick={() => navigate('/products?category=combo-packs')}
          className="group inline-flex items-center gap-2 bg-gradient-to-br from-yellow-400 to-amber-500
            text-red-900 font-black px-8 py-3.5 rounded-2xl text-sm
            shadow-xl shadow-yellow-400/30 hover:shadow-yellow-400/50 hover:-translate-y-0.5
            transition-all duration-200 relative overflow-hidden">
          <span className="absolute inset-0 translate-x-[-110%] group-hover:translate-x-[110%]
            transition-transform duration-500 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"/>
          <span className="relative">🎆 View All Combo Packs</span>
        </button>
      </div>
    </section>
  );
}

/* ─── SECTION 3: CATEGORIES ──────────────────────────────────────────────────── */
const CATS = [
  {slug:'one-sound', name:'One Sound Crackers', icon:'💥', from:'from-orange-600', to:'to-red-700'},
  {slug:'wala-items', name:'Wala Items', icon:'🎵', from:'from-pink-600', to:'to-rose-700'},
  {slug:'bomb', name:'Bomb', icon:'💣', from:'from-gray-700', to:'to-gray-900'},
  {slug:'flower-pots', name:'Flower Pots', icon:'🌸', from:'from-pink-400', to:'to-rose-600'},
  {slug:'ground', name:'Ground Chakkar', icon:'🌀', from:'from-teal-500', to:'to-cyan-600'},
  {slug:'candles', name:'Candles', icon:'🕯️', from:'from-yellow-500', to:'to-amber-600'},
  {slug:'rockets', name:'Rockets', icon:'🚀', from:'from-red-500', to:'to-red-700'},
  {slug:'fountains', name:'Fountains', icon:'⛲', from:'from-blue-400', to:'to-blue-600'},
  {slug:'showers', name:'Showers', icon:'🌟', from:'from-cyan-500', to:'to-blue-600'},
  {slug:'premium-products', name:'Premium Products', icon:'👑', from:'from-amber-500', to:'to-yellow-600'},
  {slug:'sky-shots', name:'Sky Shots', icon:'🎆', from:'from-indigo-500', to:'to-purple-600'},
  {slug:'repeating-skyshots', name:'Repeating Skyshots', icon:'🎇', from:'from-violet-500', to:'to-purple-700'},
  {slug:'sparklers', name:'Sparklers', icon:'✨', from:'from-yellow-300', to:'to-amber-400'},
  {slug:'matches', name:'Matches', icon:'🔥', from:'from-red-400', to:'to-orange-500'},
  {slug:'gift-boxes', name:'Gift Boxes', icon:'🎁', from:'from-emerald-500', to:'to-green-600'},
  {slug:'combo-packs', name:'Combo Items', icon:'🎊', from:'from-purple-500', to:'to-pink-600'},
  {slug:'kids-special', name:'Kids Special', icon:'🎈', from:'from-pink-400', to:'to-rose-500'},
];
const CATS_LOOP = [...CATS, ...CATS, ...CATS];

function Categories() {
  const navigate  = useNavigate();
  const trackRef  = useRef(null);
  const posRef    = useRef(0);
  const rafRef    = useRef(null);
  const pausedRef = useRef(false);
  const ITEM_W = 124, SET_W = CATS.length * ITEM_W;

  useEffect(() => {
    const animate = () => {
      if (!pausedRef.current) {
        posRef.current += 0.5;
        if (posRef.current >= SET_W) posRef.current = 0;
        if (trackRef.current) trackRef.current.style.transform = `translateX(-${posRef.current}px)`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [SET_W]);

  return (
    <section className="py-14 overflow-hidden" style={{background:'#fffaf0'}}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <div className="text-center reveal">
          <p className="text-red-500 text-xs font-black uppercase tracking-widest mb-3">Browse by Type</p>
          <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-2" style={{fontFamily:'"Playfair Display",serif'}}>
            Shop by Category
          </h2>
          <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mt-3"/>
        </div>
      </div>
      <div className="relative"
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; }}>
        <div className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
          style={{background:'linear-gradient(to right, #fffaf0, transparent)'}}/>
        <div className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
          style={{background:'linear-gradient(to left, #fffaf0, transparent)'}}/>
        <div className="overflow-hidden py-4">
          <div ref={trackRef} className="flex gap-4" style={{width:'max-content', willChange:'transform'}}>
            {CATS_LOOP.map((cat, i) => (
              <button key={`${cat.slug}-${i}`} onClick={() => navigate(`/products?category=${cat.slug}`)}
                className="group flex flex-col items-center gap-3 px-4 py-5 rounded-3xl flex-shrink-0 w-[108px]
                  bg-white border border-gray-100/80 shadow-[0_2px_10px_rgba(0,0,0,0.05)]
                  hover:shadow-[0_10px_28px_rgba(192,21,42,0.15)] hover:border-yellow-300/60
                  hover:-translate-y-1 active:scale-95 transition-all duration-250 cursor-pointer">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${cat.from} ${cat.to}
                  flex items-center justify-center text-[26px] shadow-md
                  group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300`}>
                  {cat.icon}
                </div>
                <span className="text-[11px] font-bold text-gray-600 text-center leading-tight
                  group-hover:text-red-700 transition-colors duration-200 whitespace-nowrap">
                  {cat.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── SECTION 4: FEATURED PRODUCTS ──────────────────────────────────────────── */
function FeaturedProducts() {
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    productsApi.featured()
      .then(res => setProducts(unwrap(res) || []))
      .catch(err => setError(err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-10">
          <div className="reveal">
            <p className="text-red-500 text-xs font-black uppercase tracking-widest mb-3">Handpicked for You</p>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900" style={{fontFamily:'"Playfair Display",serif'}}>
              Best Selling Products
            </h2>
            <div className="w-16 h-0.5 bg-gradient-to-r from-yellow-400 to-transparent mt-4"/>
          </div>
          <Link to="/products"
            className="hidden sm:flex items-center gap-2 text-red-700 font-bold text-sm hover:text-red-900 transition-colors group reveal">
            View All
            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>
        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_,i) => <ProductCardSkeleton key={i}/>)}
          </div>
        )}
        {!loading && error && (
          <div className="text-center py-12 space-y-3">
            <p className="text-4xl">😕</p>
            <p className="text-gray-600 font-semibold">Could not load products</p>
            <button onClick={load} className="text-red-600 font-bold hover:underline text-sm">Try again →</button>
          </div>
        )}
        {!loading && !error && products.length > 0 && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.slice(0,8).map((p,i) => <ProductCard key={p.id} product={p} index={i}/>)}
            </div>
            <div className="text-center mt-10">
              <Link to="/products"
                className="group relative inline-flex items-center gap-3
                  bg-gradient-to-br from-red-700 to-red-900 text-white font-black px-12 py-4 rounded-2xl
                  shadow-xl shadow-red-700/30 hover:shadow-red-700/50 hover:-translate-y-1
                  transition-all duration-300 overflow-hidden">
                <span className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%]
                  transition-transform duration-700 bg-gradient-to-r from-transparent via-white/15 to-transparent skew-x-12"/>
                🎆 View All Products
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/* ─── SECTION 5: TRUST SECTION ───────────────────────────────────────────────── */
const TRUST_PILLARS = [
  { icon:'🛡️', label:'Safety',  desc:'Supreme Court compliant. Safe packaging on every order.' },
  { icon:'✅', label:'Quality',  desc:'Quality Assured Crackers from licensed Sivakasi manufacturers.' },
  { icon:'🤝', label:'Trust',    desc:'100+ happy customers. Your satisfaction is our priority.' },
  { icon:'💬', label:'Support',  desc:'Personal response within 24 hrs via phone or WhatsApp.' },
];

function TrustSection() {
  return (
    <section className="py-14" style={{background:'#fffaf0'}}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 reveal">
          <p className="text-red-500 text-xs font-black uppercase tracking-widest mb-3">Why Choose Us</p>
          <h2 className="text-3xl sm:text-4xl font-black text-gray-900" style={{fontFamily:'"Playfair Display",serif'}}>
            Our Core Values
          </h2>
          <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mt-4"/>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {TRUST_PILLARS.map((p, i) => (
            <div key={p.label}
              className="bg-white rounded-3xl border border-gray-100 p-6 text-center
                shadow-[0_2px_12px_rgba(0,0,0,0.05)]
                hover:shadow-[0_12px_32px_rgba(192,21,42,0.1)] hover:-translate-y-1
                transition-all duration-300 reveal"
              style={{animationDelay:`${i*80}ms`}}>
              <div className="text-4xl mb-3">{p.icon}</div>
              <h3 className="font-black text-gray-900 text-base mb-2" style={{fontFamily:'"Plus Jakarta Sans",sans-serif'}}>
                {p.label}
              </h3>
              <p className="text-gray-500 text-xs leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── SECTION 6: URGENCY SALE BANNER ────────────────────────────────────────── */
function UrgencyBanner() {
  return (
    <section className="py-14 bg-gradient-to-br from-red-950 via-red-900 to-red-800 relative overflow-hidden grain">
      <div className="absolute inset-0 deco-stripes opacity-30"/>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(14)].map((_,i) => (
          <span key={i} className="absolute rounded-full animate-ping" style={{
            width:`${2+(i%4)*2}px`, height:`${2+(i%4)*2}px`,
            top:`${5+(i*19)%88}%`, left:`${2+(i*23)%95}%`,
            backgroundColor:['#fbbf24','#ef4444','#fde68a'][i%3],
            animationDuration:`${0.9+(i%4)*0.5}s`, animationDelay:`${(i%5)*0.3}s`, opacity:0.35,
          }}/>
        ))}
      </div>

      <div className="relative max-w-4xl mx-auto px-4 text-center">
        {/* Alert badge */}
        <div className="inline-flex items-center gap-2 bg-red-600/60 border border-red-400/50
          text-red-100 text-xs font-black px-5 py-2 rounded-full uppercase tracking-widest mb-6 animate-pulse">
          🚨 URGENT — Sale Ending Soon!
        </div>

        <h2 className="font-black text-white mb-4" style={{fontFamily:'"Playfair Display",serif'}}>
          <span className="block text-2xl sm:text-3xl text-yellow-300/80 font-bold mb-2">🔥 Mega Sale Alert!</span>
          <span className="block text-4xl sm:text-6xl" style={{color:'#f5c842'}}>80% Discount</span>
          <span className="block text-lg sm:text-2xl mt-2 text-white/90">on All Products!</span>
        </h2>

        <p className="text-red-200/70 text-sm sm:text-base mb-4 leading-relaxed max-w-2xl mx-auto">
          Directly from <strong className="text-yellow-300">Sivakasi</strong> — India's fireworks capital.
          Quality assured, safe packaging, affordable prices.
        </p>

        {/* Urgency message */}
        <div className="bg-red-600/30 border border-red-400/30 rounded-2xl px-6 py-4 mb-8 inline-block">
          <p className="text-white font-bold text-sm sm:text-base">
            ⏰ Coupon valid for only <span className="text-yellow-300 font-black">7 Days</span>{' '}
            · 📦 <span className="text-yellow-300 font-black">Limited Stock</span> — Book Before Sold Out!
          </p>
        </div>

        <div className="flex flex-wrap gap-4 justify-center">
          <Link to="/products"
            className="group relative inline-flex items-center gap-3
              bg-gradient-to-br from-yellow-400 to-amber-500 text-red-900 font-black px-10 py-4 rounded-2xl
              shadow-2xl shadow-yellow-400/30 hover:shadow-yellow-400/50 hover:-translate-y-1
              transition-all duration-300 overflow-hidden">
            <span className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%]
              transition-transform duration-600 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"/>
            🛒 Shop Now & Save 80%
          </Link>
          <Link to="/products?category=combo-packs"
            className="border-2 border-yellow-300/40 text-yellow-200 hover:bg-yellow-300/10
              hover:border-yellow-300/70 font-bold px-10 py-4 rounded-2xl backdrop-blur-sm
              transition-all duration-300 hover:-translate-y-0.5">
            🎆 View Combos
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─── Testimonials ───────────────────────────────────────────────────────────── */
const TESTIMONIALS = [
  {name:'Priya R.',  city:'Chennai',    stars:5, text:'Amazing quality! Delivered on time and perfectly packed. Will definitely order again for Diwali 2026.'},
  {name:'Arjun K.',  city:'Bangalore',  stars:5, text:'Best crackers shop online. The gift boxes are superb value for money and the team was very helpful.'},
  {name:'Meena I.',  city:'Coimbatore', stars:5, text:'Fast response, great packing, and the sparklers are world-class! Our kids absolutely loved them.'},
  {name:'Suresh N.', city:'Madurai',    stars:5, text:'Genuine Sivakasi crackers at fair prices. The team responded within hours and handled everything personally.'},
];
function Testimonials() {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 reveal">
          <p className="text-red-500 text-xs font-black uppercase tracking-widest mb-3">Customer Stories</p>
          <h2 className="text-3xl sm:text-4xl font-black text-gray-900" style={{fontFamily:'"Playfair Display",serif'}}>What Our Customers Say</h2>
          <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mt-4"/>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TESTIMONIALS.map((t,i) => (
            <div key={i} className="bg-gray-50 rounded-3xl p-6 border border-gray-100
              hover:shadow-[0_12px_32px_rgba(192,21,42,0.1)] hover:-translate-y-1
              transition-all duration-300 reveal" style={{animationDelay:`${i*80}ms`}}>
              <div className="flex gap-0.5 mb-3">{[...Array(t.stars)].map((_,j) => (
                <svg key={j} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              ))}</div>
              <div className="text-4xl text-yellow-400/30 font-serif leading-none mb-2">"</div>
              <p className="text-gray-600 text-sm mb-4 leading-relaxed">{t.text}</p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-100 to-amber-100
                  flex items-center justify-center text-red-700 font-black text-sm">
                  {t.name[0]}
                </div>
                <div>
                  <p className="font-bold text-gray-800 text-sm">{t.name}</p>
                  <p className="text-gray-400 text-xs">📍 {t.city}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Main Page ──────────────────────────────────────────────────────────────── */
export default function HomePage() {
  useScrollReveal();

  // ── SEO: update page title and meta description ───────────────
  React.useEffect(() => {
    document.title = 'Lakshmi Crackers — Buy Diwali Crackers Online | Sivakasi Fireworks';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Buy premium Diwali crackers online from Lakshmi Crackers, Sivakasi. Flat 80% OFF on sparklers, rockets, sky shots, flower pots, gift boxes & combo packs. Quality assured, safe packaging, affordable price.');
    return () => {
      document.title = 'Lakshmi Crackers 🪔 — Premium Fireworks';
    };
  }, []);
  return (
    <div className="page-enter">
      <AnnouncementBar/>
      <BannerSlider/>
      <ComboPacks/>
      <Categories/>
      <FeaturedProducts/>
      <TrustSection/>
      <UrgencyBanner/>
      <Testimonials/>
    </div>
  );
}
